import {
  decodeJwt,
  getLatestRcsResources,
  getTileType,
  tileTypeDisplayMap,
  rewriteResourceURL,
  useIsOnline,
} from '@readcloud/common';
import { Modal, ModalBody, ModalHeader } from '@readcloud/component-library';
import { Book } from '@readcloud/data';
import { useIsBookCached, deleteBookCache } from '@readcloud/fetch-wrapper';
import unescape from 'lodash/unescape';
import * as React from 'react';
import sanitizeHtml from 'sanitize-html';
import { ConfirmationPopoverButton } from './ConfirmationPopoverButton';
import { TileImage } from './TileImage';
import { LoadingIcon } from './LoadingIcon/LoadingIcon';
import { AnimatedMount } from './AnimatedMount';

// Check if running in Tauri environment
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;
function sanitize(text: string) {
  return sanitizeHtml(unescape(text));
}

export interface BookProps {
  book: Book;
  onClick?: () => void;
  onRemoveFromCloud?: () => Promise<unknown>;
  canRemoveFromCloud: boolean;
  hideTitle: boolean;
  size: 'small' | 'medium' | 'large' | undefined;
  className?: string;
  onRefresh?: () => void;
  onContentSheetClick?: ()=> void;
  isMyContent?: boolean;
}

/**
 * Figures out which thumbnail to display.
 * Either the one emdedded in the RCS data or the one in Thumbnail URI
 * @param uri
 * @param rcsData
 */
const getImage = (book: Book) => {
  if (book.rcsData && book.rcsData.resources) {
    const resources = getLatestRcsResources(book.rcsData);
    if (resources.thumbnailUrl) {
      return resources.thumbnailUrl;
    }
  }
  return rewriteResourceURL(book.imageThumbnailURI);
};

/**
 * Our book tile
 * @param props
 */
export const Tile: React.FC<BookProps> = (props) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const isOnline = useIsOnline();
  const image = getImage(props.book);

  /**
   * Setup Logic for whether the book is available to user.
   */
  let isBookValid = true;
  if (props.book.downloadURI) {
    isBookValid = true;
  } else if (props.book.pdfURI && props.book.pdfURI !== 'nonBook') {
    isBookValid = true;
  } else if (props.book.rcsData) {
    const decodedLicenceToken: any =
      props.book.rcsData && props.book.rcsData.licenceToken
        ? decodeJwt(props.book.rcsData.licenceToken)
        : null;

    if (
      decodedLicenceToken &&
      decodedLicenceToken.payload &&
      (Math.floor(decodedLicenceToken.payload.expireDate) >=
        Math.floor(new Date().getTime()) ||
        decodedLicenceToken.payload.expireDate == null)
    ) {
      isBookValid = true;
    } else {
      isBookValid = false;
    }
  } else {
    isBookValid = false;
  }

  const rcsUrl = props?.book?.rcsData
    ? // TODO: We need to use a non-default base RCS url here
      getLatestRcsResources(props.book.rcsData)?.bookbytesUrl
    : undefined;
  const contentUrl = rcsUrl || props.book.downloadURI;
  const tileType = contentUrl ? getTileType(contentUrl) : 'UNKNOWN';

  // Check if content is cached (only in Tauri environment)
  const { isCached, refresh: refreshCacheStatus } = useIsBookCached(contentUrl ?? undefined);
  const [isRemovingCache, setIsRemovingCache] = React.useState(false);

  const handleRemoveOfflineCopy = async () => {
    if (!contentUrl) return;
    setIsRemovingCache(true);
    try {
      await deleteBookCache(contentUrl);
      refreshCacheStatus();
    } catch (error) {
      console.error('Failed to remove offline copy:', error);
    } finally {
      setIsRemovingCache(false);
    }
  };

  const tileTypeDisplay = tileTypeDisplayMap[tileType] ?? 'Website';

  const [bookDialogOpen, setBookDialogOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  return (
    <div data-testid={`tile-${props.book.id}`} className={props.className}>
      <>
        <TileImage
          alt={props.book.title}
          src={image ? { url: image } : 'NoImage'}
          tileType={tileTypeDisplay}
          title={props.book.title}
          locked={!isBookValid}
          onClick={props.onClick}
          onMoreClick={() => {
            setBookDialogOpen(true);
          }}
          size={props.size}
          hideTitle={props.hideTitle}
          isAvailableOffline={isCached}
          showOfflineIndicator={isTauri && tileType !== 'WEB'}
          isOffline={!isOnline}
          onContentSheetClick={props.onContentSheetClick}
          isMyContent={props.isMyContent || false}
        />
      </>
      {bookDialogOpen && (
        <Modal
          toggle={() => setBookDialogOpen((open) => !open)}
          isOpen={bookDialogOpen}
          className="rc-relative"
        >
          <ModalHeader>
            <BookModalField
              name="Title"
              value={props.book.title}
              fallback="No title"
            />
          </ModalHeader>
          <ModalBody className="readcloud-ui readcloud-ui-reset">
            <div className="rc-flex rc-flex-col rc-gap-4" ref={containerRef}>
              <span className="rc-inline-flex rc-gap-4 rc-flex-wrap">
                <BookModalField name="Type" value={tileTypeDisplay} />
                {(tileType === 'EPUB' || tileType === 'PDF') && (
                  <BookModalField
                    name="Encryption"
                    value={props.book.rcsData ? 'RCS' : 'None'}
                  />
                )}
                {props.book.isbn && (
                  <BookModalField name="ISBN" value={props.book.isbn} />
                )}
                {/* Show offline status in Tauri environment (not for websites) */}
                {isTauri && tileType !== 'WEB' && (
                  <BookModalField
                    name="Offline Status"
                    value={isCached ? 'Downloaded' : 'Not downloaded'}
                  />
                )}
              </span>
              <BookModalField
                name="Description"
                value={
                  props.book.description ? (
                    // TODO: Properly show that there is "more" content when overflow happens. Currently it's not very obvious.
                    // E.g. maybe show a "..." link that expands the text
                    <div className="rc-prose-sm rc-max-h-40 rc-overflow-y-auto">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitize(props.book.description),
                        }}
                      />
                    </div>
                  ) : null
                }
                fallback="No description"
              />
              <div className="rc-flex rc-justify-end rc-gap-2 rc-flex-wrap">
                {/* Remove offline copy button (Tauri only, not for websites) */}
                {isTauri && tileType !== 'WEB' && isCached && (
                  <ConfirmationPopoverButton
                    confirmationRequired
                    backButtonText="Cancel"
                    confirmButtonText="Yes, remove"
                    onConfirm={handleRemoveOfflineCopy}
                    prompt="Remove the offline copy of this book? You can re-download it later."
                    variant="outline"
                    disabled={isRemovingCache}
                  >
                    {isRemovingCache ? 'Removing...' : 'Remove offline copy'}
                  </ConfirmationPopoverButton>
                )}
                {props.canRemoveFromCloud ? (
                  <ConfirmationPopoverButton
                    confirmationRequired
                    backButtonText="No"
                    confirmButtonText="Yes, remove it"
                    onConfirm={() => {
                      if (props.onRemoveFromCloud) {
                        setIsLoading(true);
                        return props
                          .onRemoveFromCloud()
                          .then(() => {
                            setBookDialogOpen(false);
                          })
                          .finally(() => {
                            setIsLoading(false);
                            props.onRefresh?.();
                          });
                      } else {
                        return;
                      }
                    }}
                    prompt={`Are you sure you want to remove this book from the cloud?`}
                    variant="destructive"
                  >
                    Remove from cloud
                  </ConfirmationPopoverButton>
                ) : null}
              </div>
            </div>
          </ModalBody>
          <div className="rc-absolute rc-inset-0 rc-flex rc-justify-center rc-items-center rc-pointer-events-none rc-touch-none">
            <AnimatedMount show={isLoading}>
              <LoadingIcon loading />
            </AnimatedMount>
          </div>
        </Modal>
      )}
    </div>
  );
};
function BookModalField(props: {
  name: string;
  value: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={props.className}>
      <div className="rc-text-xs rc-text-neutral-400">{props.name}</div>
      {props.value ? (
        <div>{props.value}</div>
      ) : props.fallback ? (
        <div className="rc-text-neutral-500">{props.fallback}</div>
      ) : null}
    </div>
  );
}
